const DEFAULT = 'default';
const SHORT = 'short';
const LONG = 'long';
const FULL = 'full';
const NUMERIC = 'numeric';

const TIME_TOKENS = new Set(['Z', '+', '-']);

const HOUR = '([0-1]\\d|[2][0-3])';
const MINUTE = '([0-5]\\d)';
const SECOND = MINUTE;

const DATE = '(\\d{4})-([0]\\d|[1][0-2])-([0-2]\\d|[3][0-1])';
const TIME = `${HOUR}:${MINUTE}(:${SECOND})?(\\.(\\d+))?([+-]${HOUR}:${MINUTE}|[Z])?`;

const ISO_DATE = new RegExp(`^${DATE}[Z]?$`);
const ISO_DATETIME = new RegExp(`^${DATE}T${TIME}$`);

export const VALID_DATE_FORMATS = {
  DEFAULT,
  SHORT,
  LONG,
  FULL,
  undefined,
};

type Formats = typeof VALID_DATE_FORMATS;
type Format = Formats[keyof Formats];

interface Options {
  format?: Format;
  locale?: string;
  useBrowserTimeZone?: boolean;
}

export const VALID_FORMATS = new Set(Object.values(VALID_DATE_FORMATS));

const isNumber = (date: unknown): date is number => {
  return typeof date === 'number';
};

/**
 * Checks if `value` is a valid `Date` with following conditions:
 *  - `value` is a `Date` type.
 *  - The string form of `value` does not display `"Invalid Date"`.
 *  - The numeric equivalent of `value` is not `NaN`.
 * Or if `value` is a number with the following conditions:
 *  - is not `NaN`
 * @param value - anything, literally.
 * @returns `true` if `value` is a valid `Date` object.
 */
const isValidDate = (value: unknown) => {
  if (value instanceof Date) {
    return value.toString() !== 'Invalid Date' && !isNaN(value.valueOf());
  } else if (isNumber(value)) {
    return !isNaN(value);
  }

  return false;
};

/**
 * Attempt to convert given `locale` string to a valid BCP 47 language tag form.
 * Rely on browser language if the language is the same, but region is more specific.
 * @param locale Locale code to convert.
 */
const getValidLocale = (locale: string | undefined): string => {
  const matchedLocale =
    locale && navigator.language.includes(locale)
      ? navigator.language
      : locale || navigator.language || 'en-US';

  return matchedLocale.replace(/_/g, '-');
};

const hasTimezone = (input: string): boolean => {
  let foundT = false;
  for (let i = input.length - 1; i >= 0; i--) {
    if (input[i] === 'T') {
      foundT = true;
      continue;
    }
    if (TIME_TOKENS.has(input[i]) && !foundT) {
      return true;
    }
  }
  return false;
};

/**
 * `displayDate` takes a `Date` object and a format specifier, and returns a string representing date that is formatting according to format for the user's locale.
 * @param date - `Date` object to display as `string`.
 * @param options - options object can contain following fields:
 *   * `format` - either "default", "short", "long", or "full".
 *   * `locale` - developer designated locale (this will take precedence over browser setting, if language differs).
 *   * `useBrowserTimeZone` - use browser's timezone if true else use UTC timezone.  Defaults to true
 * @returns a locale date string corresponding to current user's locale and format options.
 */
export const displayDate = (
  date: Date | number,
  options: Options = {}
): string => {
  if (!isValidDate(date)) {
    throw new TypeError(
      `The first argument must be a valid date but was "${date}".`
    );
  }

  const { format, locale, useBrowserTimeZone } = options;

  if (!VALID_FORMATS.has(format)) {
    const supported = Array.from(VALID_FORMATS)
      .filter((f) => !!f)
      .map((f) => JSON.stringify(f));
    supported[2] = `or ${supported[2]}`;
    throw new TypeError(
      `The format must be either: ${supported.join(', ')} but was "${format}".`
    );
  }

  const _options: Intl.DateTimeFormatOptions = {};
  _options.day = NUMERIC;
  _options.year = NUMERIC;

  switch (format) {
    case FULL:
      _options.weekday = LONG;
      _options.month = LONG;
      break;
    case LONG:
      _options.month = LONG;
      break;
    case SHORT:
      _options.month = NUMERIC;
      break;
    case DEFAULT:
    default:
      _options.month = SHORT;
  }

  if (useBrowserTimeZone === false) {
    _options.timeZone = 'UTC';
  }

  const localeId = getValidLocale(locale);
  const finalDate = isNumber(date) ? new Date(date) : date;

  return finalDate.toLocaleDateString(localeId, _options);
};

/**
 * `displayTime` takes a `Date` object and a format specifier, and returns a string representing time that is formatting according to format for the user's locale.
 * @param date - `Date` object to display as `string`.
 * @param options - options object can contain following fields:
 *   * `format` - either "short", "long", or "full".
 *   * `locale` - developer designated locale (this will take precedence over global value).
 *   * `useBrowserTimeZone` - use browser's timezone if true else use UTC timezone.
 * @returns a locale date string corresponding to current user's locale and format options.
 */
export const displayTime = (date: Date, options: Options = {}): string => {
  if (!isValidDate(date)) {
    throw new TypeError('The first argument must be a valid date.');
  }

  const { format, locale, useBrowserTimeZone } = options;

  if (!VALID_FORMATS.has(format)) {
    const supported = Array.from(VALID_FORMATS)
      .filter((f) => !!f)
      .map((f) => JSON.stringify(f));
    supported[2] = `or ${supported[2]}`;
    throw new TypeError(`The format must be either: ${supported.join(',')}.`);
  }

  const _options: Intl.DateTimeFormatOptions = {};
  _options.second = NUMERIC;
  _options.minute = NUMERIC;
  _options.hour = NUMERIC;

  if (format === FULL || format === LONG) {
    _options.timeZoneName = SHORT;
  } else if (format === SHORT) {
    delete _options.second;
  }

  if (useBrowserTimeZone === false) {
    _options.timeZone = 'UTC';
  }

  const localeId = getValidLocale(locale);
  return date.toLocaleTimeString(localeId, _options);
};

/**
 * `parseDate` converts `input` to native JS Date object.
 * @param input - UNIX epoch in milliseconds or ISO 8601.
 * @returns `Date` equivalent of `input`.
 */
export const parseDate = (input: number | string): Date => {
  // temporary typeguarding until all codes are converted to TS.
  if (typeof input !== 'number' && typeof input !== 'string') {
    throw new TypeError(
      `'input' must be either number or string. Got: ${input}`
    );
  }

  let _input: number | string = input;
  if (typeof _input === 'string') {
    if (ISO_DATETIME.test(_input) || ISO_DATE.test(_input)) {
      _input = hasTimezone(_input) ? _input : `${_input}Z`;
    }
  }

  const result = new Date(_input);

  if (!isValidDate(result)) {
    throw new Error(`${JSON.stringify(input)} is not a parsable date.`);
  }

  return result;
};
